"use client";

import { useRef } from "react";
import { Check, CheckCheck, CircleHelp, Clock, X, type LucideIcon } from "lucide-react";
import { STATUS_COLOR, statusGuide, type StatusTone } from "./bookingStatus";

/**
 * A booking's status as a **colour badge** (design-system.md §5): a filled
 * circle in the outcome's colour with a glyph in it, and no words.
 *
 * The words did not leave, they moved: the badge is an `img` labelled with the
 * status, so a screen reader and a hover still say it, and `StatusGuide`
 * explains every colour in one place. The glyph is what keeps meaning off
 * colour alone (§10): a clock, a tick, a double tick and a cross are told apart
 * by somebody who cannot tell the fills apart.
 */
const GLYPH: Record<StatusTone, LucideIcon> = {
  warning: Clock,
  info: Check,
  success: Check,
  muted: CheckCheck,
  danger: X,
};

export function StatusColorBadge({
  tone,
  label,
  size = "md",
}: {
  tone: StatusTone;
  label: string;
  size?: "md" | "lg";
}) {
  const Glyph = GLYPH[tone];
  const box = size === "lg" ? "size-8" : "size-6";
  const glyph = size === "lg" ? "size-4.5" : "size-3.5";
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={`inline-flex shrink-0 items-center justify-center rounded-full text-surface shadow-sm ring-2 ring-surface ${box} ${STATUS_COLOR[tone].fill}`}
    >
      <Glyph className={glyph} strokeWidth={2.6} aria-hidden />
    </span>
  );
}

/**
 * The guide button that sits beside a bookings screen's title, and the dialog
 * it opens: each colour, what it means, and which booking states wear it.
 *
 * A native `<dialog>` opened with `showModal()`: it renders in the top layer,
 * so no `backdrop-filter` ancestor can trap it (§12), and it brings Escape,
 * focus containment and the backdrop with it.
 */
export function StatusGuide() {
  const dialog = useRef<HTMLDialogElement>(null);
  const entries = statusGuide();

  return (
    <>
      <button
        type="button"
        onClick={() => dialog.current?.showModal()}
        aria-haspopup="dialog"
        className="inline-flex items-center gap-1.5 rounded-lg border border-accent/30 bg-accent/8 px-2.5 py-1 text-sm font-medium text-accent transition-colors hover:bg-accent/15"
      >
        <CircleHelp className="size-4" aria-hidden />
        Colour guide
      </button>
      <dialog
        ref={dialog}
        aria-labelledby="status-guide-title"
        // A click on the backdrop lands on the dialog element itself.
        onClick={(event) => {
          if (event.target === event.currentTarget) dialog.current?.close();
        }}
        className="m-auto w-[calc(100%-2rem)] max-w-md rounded-2xl border border-line bg-surface p-0 text-body shadow-[var(--shadow-lift)] backdrop:bg-ink/40"
      >
        <div className="p-5">
          <div className="mb-1 flex items-center justify-between">
            <h2 id="status-guide-title" className="text-base font-bold text-ink">
              Booking status colours
            </h2>
            <button
              type="button"
              onClick={() => dialog.current?.close()}
              aria-label="Close colour guide"
              className="-me-1 inline-flex size-8 items-center justify-center rounded-lg text-muted transition-colors hover:bg-page hover:text-ink"
            >
              <X className="size-4.5" aria-hidden />
            </button>
          </div>
          <p className="mb-4 text-sm text-muted">
            Each booking shows its status as a coloured badge.
          </p>
          <ul className="space-y-3">
            {entries.map((entry) => (
              <li
                key={entry.key}
                className={`flex gap-3 rounded-xl border border-line border-l-4 bg-page p-3 ${STATUS_COLOR[entry.tone].stripe}`}
              >
                <StatusColorBadge tone={entry.tone} label={entry.label} size="lg" />
                <div className="min-w-0">
                  <p className="font-semibold text-ink">{entry.label}</p>
                  <p className="mt-0.5 text-sm text-body">{entry.meaning}</p>
                  <p className="mt-2 flex flex-wrap gap-1.5">
                    {entry.states.map((state) => (
                      <span
                        key={state}
                        className="rounded-md bg-surface px-2 py-0.5 font-mono text-xs text-muted ring-1 ring-line"
                      >
                        {state}
                      </span>
                    ))}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </dialog>
    </>
  );
}
